import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SectionRenderer from '../sections/SectionRenderer';
import StatusPage from './StatusPage';
import ArticlePage from './ArticlePage';
import { api, ApiError, type ApiPage } from '../lib/api';
import { applyHead, absoluteUrl } from '../lib/head';
import { mediaUrl } from '../lib/config';
import { useSite } from '../lib/site-context';
import { isPreviewing } from '../lib/preview';

type State =
  | { kind: 'loading' }
  | { kind: 'page'; page: ApiPage }
  | { kind: 'article'; slug: string }
  | { kind: 'notFound' }
  | { kind: 'error' };

/**
 * Resolves a path to a page, or to an article.
 *
 * Slugs are region- and language-specific, so nothing is hardcoded here: `/pl/aktualnosci`
 * is a page in Polish and something else entirely in Czech, and the frontend is never told
 * which word means "news".
 *
 * **The list of pages decides, not the API.** This used to ask for the path as a page and
 * treat a 404 as "then it must be an article", which worked and cost every article view a
 * wasted round trip and a 404 in the console. `pages` already holds every published page
 * of this region in every language - it is fetched once when the app starts, for the
 * sitemap and the fallback menu - so the question can be answered without asking anybody.
 *
 * That leaves the case where the list is not there. While it is still in flight this waits
 * rather than guessing, which costs nothing: the request went out with the app's first
 * render, so it lands at about the moment the old speculative page request would have come
 * back 404. And if it *failed*, there is nothing to decide from, so the old
 * ask-then-fall-back behaviour is what happens - a broken list should degrade to a slower
 * site, not a site that calls every page an article.
 *
 * The fall-back to the last segment is what keeps `/pl/<slug>` working as an article while
 * an editor has not yet ticked a page as the news index.
 *
 * **Except in preview, where the list is not the whole truth.** It holds published pages
 * only, and a draft that has never been published is exactly what an editor opens the
 * preview panel to look at: not on the list, so it was treated as an article, the article
 * lookup came back 404, and the panel said "not found" without the page endpoint - which
 * answers with the draft when it sees the preview token - ever being asked. Found on
 * 2026-09-24 with the first page the editor assistant drafted, but true of every page
 * created by hand and not yet published. So while previewing the page is asked for first,
 * and only a 404 means article; one extra request, for editors only.
 */
export default function DynamicPage() {
  const { region, locale, pages, pagesStatus, t } = useSite();
  const { pathname } = useLocation();
  const [state, setState] = useState<State>({ kind: 'loading' });

  const segments = pathname.split('/').filter(Boolean).slice(1); // drop the locale
  const slug = segments.length ? segments.join('/') : 'home';
  const lastSegment = segments.length ? segments[segments.length - 1] : '';

  // Is there a published page at this path, in this language? `pages` carries every one of
  // them, layout templates included, so "not in the list" is an answer and not a maybe.
  const isKnownPage = pages.some((p) => p.slug === slug && p.locale === locale);
  const previewing = isPreviewing();

  useEffect(() => {
    if (pagesStatus === 'loading') return undefined;

    let cancelled = false;
    setState({ kind: 'loading' });

    // The list says this is not a page, so do not ask whether it is one - unless this is a
    // preview, where an unpublished page is by definition not on the list. See above.
    if (pagesStatus === 'ready' && !isKnownPage && !previewing) {
      setState(lastSegment ? { kind: 'article', slug: lastSegment } : { kind: 'notFound' });
      return undefined;
    }

    api.page(region.code, locale, slug)
      .then((page) => { if (!cancelled) setState({ kind: 'page', page }); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Reachable with no usable list - it failed to load, or a page was unpublished
          // since it was fetched - and in preview, for every path that is an article.
          setState(lastSegment ? { kind: 'article', slug: lastSegment } : { kind: 'notFound' });
          return;
        }
        setState({ kind: 'error' });
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region.code, locale, slug, pagesStatus, isKnownPage, previewing]);

  useEffect(() => {
    if (state.kind === 'page') {
      const page = state.page;
      const path = page.isHomepage ? `/${locale}/` : `/${locale}/${page.slug}`;

      // hreflang stays inside the region: regions do not share slugs, so a Polish page
      // has no Czech counterpart to point at. Cross-domain alternates would need the
      // client to say which pages are translations of one another.
      const alternates = pages
        .filter((p) => p.slug === page.slug && p.locale !== locale)
        .map((p) => ({
          locale: p.locale,
          url: absoluteUrl(region.domain, p.isHomepage ? `/${p.locale}/` : `/${p.locale}/${p.slug}`)
        }))
        .filter((alt) => alt.url);

      const canonical = absoluteUrl(region.domain, path);
      applyHead({
        title: page.seoTitle || page.title,
        description: page.description,
        canonical: canonical || undefined,
        alternates: canonical ? [{ locale, url: canonical }, ...alternates] : [],
        ogImage: page.ogImage ? mediaUrl(page.ogImage.url) : undefined,
        // Two reasons a published page should not be found. An article-layout page is
        // a template, not a destination: on its own URL it draws the sections around an
        // article that is not there. And `noindex` is the editor's own switch, which the
        // legal placeholders waiting on their documents use. The sitemap leaves both out.
        noindex: Boolean(page.noindex) || page.articleLayoutFor !== 'none'
      });
    }
  }, [state, locale, pages, region.domain]);

  if (state.kind === 'loading') return <StatusPage title={t('page.loading')} body="" />;
  if (state.kind === 'article') return <ArticlePage slug={state.slug} />;
  if (state.kind === 'notFound') return <StatusPage notFound title={t('page.notFound')} body={t('page.notFoundBody')} />;
  if (state.kind === 'error') return <StatusPage title={t('page.error')} body={t('page.errorBody')} />;

  return (
    <>
      {state.page.sections.map((section, i) => (
        <SectionRenderer key={`${section.__component}-${section.id}`} section={section} first={i === 0} />
      ))}
    </>
  );
}
